import React, { useState } from "react";
import { FiArrowDown, FiArrowUp, FiCheck, FiChevronRight, FiFile, FiFileText, FiFolder, FiImage } from "react-icons/fi";
import type { StorageFolder, StorageObject } from "../../API";
import { formatExactDateFr, formatRelativeTimeFr } from "../../Helper/dates";
import type { FileKind } from "./utils";
import { buildPublicUrl, fileKindLabel, formatFileSize, resolveDisplayKey, resolveFileKind } from "./utils";
import type { SortDir, SortKey } from "./types";
import { ROOT_PATHS } from "./types";
import { IconDocumentStack, IconFolder, IconImageStack } from "./StorageIcons";

// ---------------------------------------------------------------------------
// Visuels partagés (icônes typées, vignettes)
// ---------------------------------------------------------------------------

/** Icône + couleurs par type de fichier : utilisée par la grille, la liste et le panneau de détail. */
export const fileKindVisuals = (kind: FileKind): { accentClass: string; icon: React.ReactNode } => {
	switch (kind) {
		case "image":
			return { accentClass: "bg-amber-50 text-amber-600", icon: <FiImage className="h-6 w-6" /> };
		case "pdf":
			return { accentClass: "bg-rose-50 text-rose-500", icon: <FiFileText className="h-6 w-6" /> };
		case "document":
			return { accentClass: "bg-sky-50 text-sky-600", icon: <FiFileText className="h-6 w-6" /> };
		default:
			return { accentClass: "bg-slate-100 text-slate-500", icon: <FiFile className="h-6 w-6" /> };
	}
};

export const folderVisuals = (path: string, kind?: string | null) => {
	const rootMeta = ROOT_PATHS.find((root) => root.path === path);
	if (rootMeta) {
		const accentClass = rootMeta.icon === "image" ? "bg-amber-100 text-amber-600" : "bg-sky-100 text-sky-600";
		const IconComponent = rootMeta.icon === "image" ? IconImageStack : IconDocumentStack;
		return { accentClass, iconElement: <IconComponent className="h-8 w-8" /> };
	}
	if ((kind ?? "").toLowerCase().includes("publication")) {
		return { accentClass: "bg-sky-100 text-sky-600", iconElement: <IconDocumentStack className="h-8 w-8" /> };
	}
	return { accentClass: "bg-slate-100 text-slate-500", iconElement: <IconFolder className="h-8 w-9" /> };
};

/**
 * Vignette réelle pour les images (URL publique du storage, chargement paresseux), icône typée
 * pour tout le reste. Une image qui ne charge pas (clé cassée, storage privé) retombe sur l'icône
 * plutôt que d'afficher l'icône d'image cassée du navigateur.
 */
export const FileThumb: React.FC<{ object: StorageObject; className?: string }> = ({ object, className = "h-28" }) => {
	const [failed, setFailed] = useState(false);
	const kind = resolveFileKind(object);
	const url = kind === "image" && !failed ? buildPublicUrl(resolveDisplayKey(object)) : null;
	if (url) {
		return (
			<div className={`w-full overflow-hidden rounded-xl bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#ffffff_0%_50%)] bg-[length:16px_16px] ${className}`}>
				<img
					src={url}
					alt={object.title ?? object.fileName ?? "Aperçu"}
					loading="lazy"
					onError={() => setFailed(true)}
					className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.03]"
				/>
			</div>
		);
	}
	const { accentClass, icon } = fileKindVisuals(kind);
	return (
		<div className={`flex w-full items-center justify-center rounded-xl ${accentClass} ${className}`}>
			<span className="[&>svg]:h-9 [&>svg]:w-9">{icon}</span>
		</div>
	);
};

// ---------------------------------------------------------------------------
// Cartes de dossiers
// ---------------------------------------------------------------------------

export const FolderCard: React.FC<{
	folder: StorageFolder;
	onOpen: (path: string) => void;
	subtitle?: string | null;
}> = ({ folder, onOpen, subtitle }) => {
	const path = folder.path ?? "";
	const { accentClass, iconElement } = folderVisuals(path, folder.kind);
	return (
		<button
			type="button"
			onClick={() => path && onOpen(path)}
			className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition hover:-translate-y-px hover:border-slate-300 hover:shadow-md"
		>
			<span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${accentClass}`}>{iconElement}</span>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-sm font-semibold text-slate-900">{folder.name ?? path ?? "Dossier"}</span>
				<span className="mt-0.5 block truncate text-xs text-slate-500">{subtitle ?? path ?? "-"}</span>
			</span>
			<FiChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-slate-500" aria-hidden />
		</button>
	);
};

// ---------------------------------------------------------------------------
// Badges de taxonomies (grille + liste)
// ---------------------------------------------------------------------------

/** Deux premières taxonomies en pastilles + « +n » : noms résolus via la map fournie. */
export const TaxonomyBadges: React.FC<{
	taxonomyIds?: Array<string | null> | null;
	taxonomyNameById?: Map<string, string>;
}> = ({ taxonomyIds, taxonomyNameById }) => {
	const ids = (taxonomyIds ?? []).filter((id): id is string => Boolean(id));
	if (!ids.length || !taxonomyNameById) return null;
	const visible = ids.slice(0, 2);
	const overflow = ids.length - visible.length;
	return (
		<span className="mt-1 flex flex-wrap items-center gap-1">
			{visible.map((id) => (
				<span key={id} className="max-w-[9rem] truncate rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
					{taxonomyNameById.get(id) ?? id}
				</span>
			))}
			{overflow > 0 && (
				<span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400">+{overflow}</span>
			)}
		</span>
	);
};

/** Case de sélection multiple superposée à une tuile (mode manage). */
const BulkCheck: React.FC<{ checked: boolean; label: string; onToggle: () => void; overlay?: boolean }> = ({
	checked,
	label,
	onToggle,
	overlay = false
}) => (
	<button
		type="button"
		role="checkbox"
		aria-checked={checked}
		aria-label={label}
		onClick={(event) => {
			event.stopPropagation();
			onToggle();
		}}
		className={`${
			overlay
				? `absolute left-2 top-2 z-10 transition ${checked ? "opacity-100" : "opacity-0 focus:opacity-100 group-hover:opacity-100"}`
				: ""
		} flex h-5 w-5 items-center justify-center rounded border shadow-sm ${
			checked ? "border-[#005CA9] bg-[#005CA9] text-white" : "border-slate-300 bg-white/95 text-transparent hover:border-slate-400"
		}`}
	>
		<FiCheck className="h-3.5 w-3.5" aria-hidden />
	</button>
);

// ---------------------------------------------------------------------------
// Grille de fichiers
// ---------------------------------------------------------------------------

export const FileGrid: React.FC<{
	objects: StorageObject[];
	selectedId: string | null;
	onSelect: (object: StorageObject) => void;
	/** En vue « tous les médias », chaque tuile affiche aussi son dossier d'origine. */
	showFolder?: boolean;
	/** Noms des taxonomies (badges sur les tuiles). */
	taxonomyNameById?: Map<string, string>;
	/** Sélection multiple (mode manage) : ids cochés + bascule. */
	bulkSelectedIds?: ReadonlySet<string>;
	onToggleBulk?: (object: StorageObject) => void;
}> = ({ objects, selectedId, onSelect, showFolder, taxonomyNameById, bulkSelectedIds, onToggleBulk }) => (
	<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
		{objects.map((object, index) => {
			const kind = resolveFileKind(object);
			const selected = Boolean(selectedId && object.id === selectedId);
			const bulkChecked = Boolean(object.id && bulkSelectedIds?.has(object.id));
			return (
				<div key={object.id ?? object.key ?? index} className="group relative">
					{onToggleBulk && object.id && (
						<BulkCheck
							overlay
							checked={bulkChecked}
							label={`Sélectionner « ${object.title ?? object.fileName ?? object.key ?? "fichier"} »`}
							onToggle={() => onToggleBulk(object)}
						/>
					)}
					<button
						type="button"
						onClick={() => onSelect(object)}
						aria-current={selected ? "true" : undefined}
						className={`flex h-full w-full flex-col gap-2 rounded-2xl border bg-white p-3 text-left shadow-sm transition hover:-translate-y-px hover:shadow-md ${
							selected
								? "border-[#005CA9] ring-1 ring-[#005CA9]/25"
								: bulkChecked
								? "border-[#005CA9]/50"
								: "border-slate-200 hover:border-slate-300"
						}`}
					>
						<FileThumb object={object} className="h-36" />
						<span className="min-w-0">
							<span className={`block truncate text-sm font-semibold ${selected ? "text-[#005CA9]" : "text-slate-900"}`} title={object.title ?? object.fileName ?? undefined}>
								{object.title ?? object.fileName ?? "Sans titre"}
							</span>
							<span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-400">
								<span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
									{fileKindLabel(kind)}
								</span>
								{formatFileSize(object.sizeBytes)}
								<span aria-hidden>·</span>
								<span title={formatExactDateFr(object.updatedAt ?? object.createdAt)}>{formatRelativeTimeFr(object.updatedAt ?? object.createdAt)}</span>
							</span>
							<TaxonomyBadges taxonomyIds={object.taxonomyIds} taxonomyNameById={taxonomyNameById} />
							{showFolder && <span className="mt-1 block truncate font-mono text-[10px] text-slate-400">{object.folder ?? "-"}</span>}
						</span>
					</button>
				</div>
			);
		})}
	</div>
);

// ---------------------------------------------------------------------------
// Vue liste triable
// ---------------------------------------------------------------------------

const SortHeader: React.FC<{
	label: string;
	sortKey: SortKey;
	activeKey: SortKey;
	dir: SortDir;
	onSort: (key: SortKey) => void;
	align?: "left" | "right";
}> = ({ label, sortKey, activeKey, dir, onSort, align = "left" }) => {
	const active = activeKey === sortKey;
	return (
		<th scope="col" className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}>
			<button
				type="button"
				onClick={() => onSort(sortKey)}
				aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : undefined}
				className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide transition ${
					active ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
				}`}
			>
				{label}
				{active && (dir === "asc" ? <FiArrowUp className="h-3 w-3" aria-hidden /> : <FiArrowDown className="h-3 w-3" aria-hidden />)}
			</button>
		</th>
	);
};

export const FileTable: React.FC<{
	folders: StorageFolder[];
	objects: StorageObject[];
	selectedId: string | null;
	sortKey: SortKey;
	sortDir: SortDir;
	onSort: (key: SortKey) => void;
	onOpenFolder: (path: string) => void;
	onSelect: (object: StorageObject) => void;
	showFolder?: boolean;
	/** Noms des taxonomies (badges sous le nom). */
	taxonomyNameById?: Map<string, string>;
	/** Sélection multiple (mode manage) : colonne de cases à cocher. */
	bulkSelectedIds?: ReadonlySet<string>;
	onToggleBulk?: (object: StorageObject) => void;
}> = ({ folders, objects, selectedId, sortKey, sortDir, onSort, onOpenFolder, onSelect, showFolder, taxonomyNameById, bulkSelectedIds, onToggleBulk }) => {
	const bulkColumn = Boolean(onToggleBulk);
	return (
		<div className="overflow-x-auto rounded-xl border border-slate-200">
			<table className="min-w-full divide-y divide-slate-200 text-sm">
				<thead className="bg-slate-50">
					<tr>
						{bulkColumn && (
							<th scope="col" className="w-10 px-3 py-3">
								<span className="sr-only">Sélection</span>
							</th>
						)}
						<SortHeader label="Nom" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onSort} />
						<th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
							Type
						</th>
						<SortHeader label="Taille" sortKey="size" activeKey={sortKey} dir={sortDir} onSort={onSort} />
						<SortHeader label="Modifié" sortKey="date" activeKey={sortKey} dir={sortDir} onSort={onSort} />
					</tr>
				</thead>
				<tbody className="divide-y divide-slate-100 bg-white">
					{folders.map((folder, index) => {
						const path = folder.path ?? "";
						return (
							<tr
								key={folder.id ?? path ?? `folder-${index}`}
								onClick={() => path && onOpenFolder(path)}
								className="cursor-pointer transition hover:bg-slate-50"
							>
								{bulkColumn && <td className="px-3 py-2.5" />}
								<td className="px-4 py-2.5">
									<span className="flex items-center gap-2.5">
										<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
											<FiFolder className="h-4 w-4" aria-hidden />
										</span>
										<span className="min-w-0">
											<span className="block truncate font-semibold text-slate-900">{folder.name ?? path}</span>
											<span className="block truncate text-xs text-slate-400">{path}</span>
										</span>
									</span>
								</td>
								<td className="whitespace-nowrap px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Dossier</td>
								<td className="whitespace-nowrap px-4 py-2.5 text-slate-400">-</td>
								<td className="whitespace-nowrap px-4 py-2.5 text-slate-400">-</td>
							</tr>
						);
					})}
					{objects.map((object, index) => {
						const kind = resolveFileKind(object);
						const { accentClass, icon } = fileKindVisuals(kind);
						const selected = Boolean(selectedId && object.id === selectedId);
						const bulkChecked = Boolean(object.id && bulkSelectedIds?.has(object.id));
						return (
							<tr
								key={object.id ?? object.key ?? `file-${index}`}
								onClick={() => onSelect(object)}
								aria-current={selected ? "true" : undefined}
								className={`cursor-pointer transition ${selected ? "bg-[#005CA9]/5" : bulkChecked ? "bg-[#005CA9]/[0.03]" : "hover:bg-slate-50"}`}
							>
								{bulkColumn && (
									<td className="px-3 py-2.5">
										{object.id && onToggleBulk && (
											<BulkCheck
												checked={bulkChecked}
												label={`Sélectionner « ${object.title ?? object.fileName ?? object.key ?? "fichier"} »`}
												onToggle={() => onToggleBulk(object)}
											/>
										)}
									</td>
								)}
								<td className="px-4 py-2.5">
									<span className="flex items-center gap-2.5">
										<span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${accentClass} [&>svg]:h-4 [&>svg]:w-4`}>
											{icon}
										</span>
										<span className="min-w-0">
											<span className={`block truncate font-semibold ${selected ? "text-[#005CA9]" : "text-slate-900"}`}>
												{object.title ?? object.fileName ?? "Sans titre"}
											</span>
											<span className="block truncate text-xs text-slate-400">
												{showFolder ? object.folder ?? object.fileName ?? "-" : object.fileName ?? "-"}
											</span>
											<TaxonomyBadges taxonomyIds={object.taxonomyIds} taxonomyNameById={taxonomyNameById} />
										</span>
									</span>
								</td>
								<td className="whitespace-nowrap px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
									{fileKindLabel(kind)}
								</td>
								<td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-slate-700">{formatFileSize(object.sizeBytes)}</td>
								<td className="whitespace-nowrap px-4 py-2.5 text-slate-600" title={formatExactDateFr(object.updatedAt ?? object.createdAt)}>
									{formatRelativeTimeFr(object.updatedAt ?? object.createdAt)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

// ---------------------------------------------------------------------------
// Squelettes + état vide
// ---------------------------------------------------------------------------

export const GridSkeleton: React.FC<{ tiles?: number }> = ({ tiles = 10 }) => (
	<div className="grid animate-pulse gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5" aria-hidden>
		{Array.from({ length: tiles }).map((_, index) => (
			<div key={index} className="rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
				<div className="h-28 rounded-xl bg-slate-100" />
				<div className="mt-2.5 h-3.5 w-3/4 rounded bg-slate-100" />
				<div className="mt-1.5 h-3 w-1/3 rounded bg-slate-100" />
			</div>
		))}
	</div>
);

export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 8 }) => (
	<div className="animate-pulse space-y-2 rounded-xl border border-slate-100 bg-white p-4" aria-hidden>
		{Array.from({ length: rows }).map((_, index) => (
			<div key={index} className="flex items-center gap-3">
				<div className="h-8 w-8 rounded-lg bg-slate-100" />
				<div className="h-3.5 flex-1 rounded bg-slate-100" />
				<div className="h-3.5 w-16 rounded bg-slate-100" />
				<div className="h-3.5 w-24 rounded bg-slate-100" />
			</div>
		))}
	</div>
);

export const EmptyState: React.FC<{ icon: React.ReactNode; title: string; hint?: string; children?: React.ReactNode }> = ({
	icon,
	title,
	hint,
	children
}) => (
	<div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
		<span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 [&>svg]:h-6 [&>svg]:w-6">
			{icon}
		</span>
		<p className="mt-3 text-sm font-semibold text-slate-700">{title}</p>
		{hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
		{children}
	</div>
);
